/**
 * Frontend service for the location-management Medplum Bot.
 *
 * The Bot must be registered in Medplum with:
 *   identifier: { system: 'https://unify.health/bots', value: 'location-management' }
 *   runtimeVersion: 'vmcontext'
 */

import { ContentType, OperationOutcomeError } from '@medplum/core';
import type { MedplumClient } from '@medplum/core';
import type { Identifier } from '@medplum/fhirtypes';
import { UserFacingError } from './userFacingError';

const LOCATION_MANAGEMENT_BOT_IDENTIFIER: Identifier = {
  system: 'https://unify.health/bots',
  value: 'location-management',
};

function getBotTarget(): string | Identifier {
  const id = import.meta.env.VITE_MEDPLUM_LOCATION_MANAGEMENT_BOT_ID?.trim();
  if (id) return id;
  return LOCATION_MANAGEMENT_BOT_IDENTIFIER;
}

/**
 * Allowlist of the bot's authored "appointment still uses it" delete rejections. Mirrors the
 * templates in medplum-server/src/bots/location-management.ts verbatim; update both together.
 */
export const ROOM_BLOCKED_CLAUSE =
  ' cannot be deleted while an appointment still uses it. Disable the room instead to prevent new bookings.';
export const EQUIPMENT_BLOCKED_CLAUSE =
  ' cannot be deleted while an appointment still uses it. Disable the equipment instead to prevent new bookings.';
export const LOCATION_BLOCKED_CLAUSE =
  ' cannot be deleted while an appointment still uses it or its rooms or equipment. Disable the location instead to prevent new bookings.';

const NAME = '([^\\n]+?)';
const escapeRegExp = (text: string): string => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const BLOCKED_DELETE_PATTERNS = [
  new RegExp(`^Room ${NAME}${escapeRegExp(ROOM_BLOCKED_CLAUSE)}$`),
  new RegExp(`^Equipment ${NAME}${escapeRegExp(EQUIPMENT_BLOCKED_CLAUSE)}$`),
  new RegExp(`^Location ${NAME}${escapeRegExp(LOCATION_BLOCKED_CLAUSE)}$`),
];

function blockedDeleteText(err: unknown): string | undefined {
  if (!(err instanceof OperationOutcomeError)) return undefined;
  const issue = err.outcome.issue?.[0];
  for (const text of [issue?.details?.text, issue?.diagnostics]) {
    if (text && BLOCKED_DELETE_PATTERNS.some((pattern) => pattern.test(text))) return text;
  }
  return undefined;
}

/** Runs a delete action; surfaces the bot's blocked-delete guidance as a `UserFacingError`. */
async function executeDelete<T>(medplum: MedplumClient, input: Record<string, string>): Promise<T> {
  try {
    return (await medplum.executeBot(getBotTarget(), input, ContentType.JSON)) as T;
  } catch (err) {
    const text = blockedDeleteText(err);
    throw text ? new UserFacingError(text) : err;
  }
}

export async function deleteRoom(
  medplum: MedplumClient,
  roomId: string,
): Promise<{ success: true; deletedId: string; reassignedDevices: string[] }> {
  return executeDelete(medplum, { action: 'delete-room', roomId });
}

export async function deleteEquipment(
  medplum: MedplumClient,
  equipmentId: string,
): Promise<{ success: true; deletedId: string }> {
  return executeDelete(medplum, { action: 'delete-equipment', equipmentId });
}

export async function deleteSite(
  medplum: MedplumClient,
  siteId: string,
): Promise<{ success: true; deletedId: string; deletedRooms: string[]; deletedDevices: string[] }> {
  return executeDelete(medplum, { action: 'delete-site', siteId });
}
